import React, { useEffect, useState } from "react";
import { getAnalyticsSummary, getAdminUsers } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BarChart3, ChevronRight, ChevronDown, Globe, Smartphone, Monitor, Users, Eye, LogIn, ShieldAlert, UserCheck, Activity } from "lucide-react";

const DAYS = [7, 30, 90];

const BarList = ({ title, icon: Icon, items, color = "bg-blue-500" }) => {
  const max = Math.max(1, ...(items || []).map((i) => i.count));
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
        <Icon className="h-4 w-4 text-blue-400" /> {title}
      </div>
      <div className="mt-3 space-y-2">
        {(items || []).length === 0 && <div className="text-[12px] text-zinc-600">No data yet</div>}
        {(items || []).map((i) => (
          <div key={i.key} className="text-[12px]">
            <div className="flex justify-between text-zinc-300"><span className="truncate pr-2">{i.key}</span><span className="font-mono text-zinc-400">{i.count}</span></div>
            <div className="mt-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${(i.count / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState(null);
  const [scope, setScope] = useState("mine");
  const [days, setDays] = useState(30);
  const [expanded, setExpanded] = useState(null);
  const isAdmin = user?.is_admin;

  useEffect(() => { setData(null); getAnalyticsSummary(scope, days).then(setData).catch(() => setData(null)); }, [scope, days]);
  useEffect(() => { if (isAdmin) getAdminUsers().then(setUsers).catch(() => setUsers(null)); }, [isAdmin]);

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
  const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

  const t = data?.totals || {};
  const maxDay = Math.max(1, ...((data?.timeseries || []).map((d) => d.count)));

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> workspace <ChevronRight className="h-3 w-3" /> analytics
        </span>
      </header>

      <div className="p-5 sm:p-8" data-testid="analytics-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold"><BarChart3 className="h-4 w-4" /> Visitor Analytics</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Who's visiting</h1>
            <p className="text-zinc-400 mt-2">Traffic, sources, devices and geography{scope === "all" ? " across all workspaces" : " for your workspace"}.</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <div className="inline-flex rounded-md border border-zinc-800 bg-zinc-900 p-0.5" data-testid="analytics-scope-switch">
                <button onClick={() => setScope("mine")} data-testid="scope-mine" className={`text-[12px] font-semibold px-3 py-1.5 rounded ${scope === "mine" ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"}`}>My workspace</button>
                <button onClick={() => setScope("all")} data-testid="scope-all" className={`text-[12px] font-semibold px-3 py-1.5 rounded ${scope === "all" ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"}`}>All (admin)</button>
              </div>
            )}
            <div className="inline-flex rounded-md border border-zinc-800 bg-zinc-900 p-0.5" data-testid="analytics-range-switch">
              {DAYS.map((d) => <button key={d} onClick={() => setDays(d)} data-testid={`range-${d}`} className={`text-[12px] font-semibold px-3 py-1.5 rounded ${days === d ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"}`}>{d}d</button>)}
            </div>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          {[
            { label: "Visits", value: t.visits ?? "—", icon: Eye },
            { label: "Unique visitors", value: t.unique_visitors ?? "—", icon: Users },
            { label: "Logged-in views", value: t.logged_in ?? "—", icon: LogIn },
            { label: "Blocked regions", value: (t.blocked_countries || []).length, icon: ShieldAlert },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <s.icon className="h-4 w-4 text-blue-400" />
              <div className="font-heading text-3xl font-extrabold text-white mt-2">{s.value}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Daily timeseries */}
        <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Daily visits</div>
          <div className="mt-4 flex items-end gap-1 h-28">
            {(data?.timeseries || []).length === 0 && <div className="text-[12px] text-zinc-600">No data yet</div>}
            {(data?.timeseries || []).map((d) => (
              <div key={d.day} className="flex-1 bg-blue-600/70 hover:bg-blue-500 rounded-t" style={{ height: `${(d.count / maxDay) * 100}%` }} title={`${d.day}: ${d.count}`} />
            ))}
          </div>
        </div>

        {/* Breakdowns */}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mt-4">
          <BarList title="Top countries" icon={Globe} items={data?.by_country} color="bg-emerald-500" />
          <BarList title="Traffic sources" icon={ChevronRight} items={data?.by_referrer} color="bg-blue-500" />
          <BarList title="Top pages" icon={Eye} items={data?.top_pages} color="bg-violet-500" />
          <BarList title="Devices" icon={Smartphone} items={data?.by_device} color="bg-amber-500" />
          <BarList title="Browsers" icon={Monitor} items={data?.by_browser} color="bg-sky-500" />
          <BarList title="Operating systems" icon={Monitor} items={data?.by_os} color="bg-rose-500" />
        </div>

        {/* Users — real customer accounts (admin only) */}
        {isAdmin && (
          <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden" data-testid="users-section">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2 flex-wrap">
              <UserCheck className="h-4 w-4 text-emerald-400" />
              <span className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">Users — customer accounts</span>
              {users && (
                <span className="text-[11px] text-zinc-500 ml-auto font-mono">
                  {users.total} signed up · <span className="text-emerald-400">{users.pro} Pro</span> · <span className="text-blue-400">{users.free} Free</span>
                  {typeof t.anonymous_visitors === "number" && (
                    <> · <span className="text-zinc-400">{t.anonymous_visitors} visited, didn't sign up</span></>
                  )}
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[12px] min-w-[820px]" data-testid="users-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                    <th className="px-4 py-2 font-semibold">Email</th>
                    <th className="px-4 py-2 font-semibold">Name</th>
                    <th className="px-4 py-2 font-semibold">Plan</th>
                    <th className="px-4 py-2 font-semibold">Registered</th>
                    <th className="px-4 py-2 font-semibold">Last login</th>
                    <th className="px-4 py-2 font-semibold">Last active</th>
                    <th className="px-4 py-2 font-semibold">Sign-in</th>
                    <th className="px-4 py-2 font-semibold text-right">Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {(users?.users || []).map((u, i) => (
                    <React.Fragment key={i}>
                      <tr
                        className="border-b border-zinc-900 text-zinc-300 hover:bg-zinc-800/40 cursor-pointer"
                        data-testid={`user-row-${i}`}
                        onClick={() => setExpanded(expanded === i ? null : i)}
                      >
                        <td className="px-4 py-2 font-mono text-zinc-200">{u.email}</td>
                        <td className="px-4 py-2">{u.name || "—"}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${u.plan === "pro" ? "bg-emerald-600/15 text-emerald-400 border-emerald-600/30" : "bg-blue-600/15 text-blue-400 border-blue-600/30"}`}>
                            {u.plan === "pro" ? "Pro" : "Free"}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-zinc-400">{fmt(u.created_at)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-zinc-400">{u.last_login ? fmt(u.last_login) : "Never"}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-zinc-400">{u.last_active ? fmtTime(u.last_active) : "—"}</td>
                        <td className="px-4 py-2 text-zinc-500 capitalize">{u.provider === "google" ? "Google" : "Email"}</td>
                        <td className="px-4 py-2 text-right">
                          <span className="inline-flex items-center gap-1 text-zinc-400" data-testid={`user-expand-${i}`}>
                            <Activity className="h-3.5 w-3.5 text-blue-400" />
                            {u.activity_count ?? 0}
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded === i ? "rotate-180" : ""}`} />
                          </span>
                        </td>
                      </tr>
                      {expanded === i && (
                        <tr className="border-b border-zinc-900 bg-zinc-950/60" data-testid={`user-activity-${i}`}>
                          <td colSpan={8} className="px-4 py-3">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Recent activity — spot if they're stuck</div>
                            {(u.recent_activity || []).length === 0 ? (
                              <div className="text-[12px] text-zinc-600 font-mono">No in-app activity recorded yet.</div>
                            ) : (
                              <ul className="space-y-1">
                                {u.recent_activity.map((a, j) => (
                                  <li key={j} className="flex items-center gap-3 text-[12px]">
                                    <span className="text-zinc-600 font-mono whitespace-nowrap w-36 shrink-0">{fmtTime(a.ts)}</span>
                                    <ChevronRight className="h-3 w-3 text-zinc-700 shrink-0" />
                                    <span className="font-mono text-zinc-300 truncate">{a.path}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {users && users.users.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-zinc-600 font-mono">No customer sign-ups yet.</td></tr>}
                  {!users && <tr><td colSpan={8} className="px-4 py-6 text-zinc-600 font-mono">Loading users…</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent visitors */}
        <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Recent visits</div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[12px] min-w-[720px]" data-testid="recent-visits-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                  <th className="px-4 py-2 font-semibold">When</th>
                  <th className="px-4 py-2 font-semibold">Page</th>
                  <th className="px-4 py-2 font-semibold">Location</th>
                  <th className="px-4 py-2 font-semibold">Device</th>
                  <th className="px-4 py-2 font-semibold">Source</th>
                  {isAdmin && <th className="px-4 py-2 font-semibold">IP address</th>}
                  {isAdmin && scope === "all" && <th className="px-4 py-2 font-semibold">User</th>}
                </tr>
              </thead>
              <tbody>
                {(data?.recent || []).map((r, i) => (
                  <tr key={i} className="border-b border-zinc-900 text-zinc-300">
                    <td className="px-4 py-2 whitespace-nowrap text-zinc-500">{r.ts ? new Date(r.ts).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2 font-mono text-zinc-300">{r.path}</td>
                    <td className="px-4 py-2">{[r.city, r.country].filter(Boolean).join(", ") || "—"}</td>
                    <td className="px-4 py-2">{r.device} · {r.browser}/{r.os}</td>
                    <td className="px-4 py-2 truncate max-w-[160px]">{r.referrer}</td>
                    {isAdmin && <td className="px-4 py-2 font-mono text-zinc-400">{r.ip || "—"}</td>}
                    {isAdmin && scope === "all" && <td className="px-4 py-2 text-zinc-400">{r.logged_in ? (r.email || "member") : "anon"}</td>}
                  </tr>
                ))}
                {(!data || data.recent.length === 0) && <tr><td colSpan={8} className="px-4 py-6 text-zinc-600 font-mono">No visits recorded yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
